import React from 'react';
import SceneProps from './SceneProps';

const INK = '#786c5c';
const WOOD = '#b59673';
const DARK_WOOD = '#92795d';
const PAPER = '#f6efdf';
const PALETTES = [
  { main: '#94aa94', light: '#dfe7d5', second: '#c6a08b', cream: '#e9d6ab' },
  { main: '#8fa8b2', light: '#dce8e9', second: '#b99ca8', cream: '#e9d9b9' },
  { main: '#b79a88', light: '#eee1d4', second: '#9faa8f', cream: '#e7d3a2' },
  { main: '#a49caf', light: '#e5deeb', second: '#c4ad85', cream: '#eadfbe' },
];

function palette(variant) {
  const n = Number(variant);
  const index = Number.isFinite(n) ? Math.abs(Math.trunc(n)) : 0;
  return { p: PALETTES[index % PALETTES.length], index };
}

function ArtFrame({ children }) {
  return <svg viewBox="0 0 320 210" className="scene-prop-art" preserveAspectRatio="xMidYMax meet" aria-hidden="true" focusable="false" fill="none" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="160" cy="188" rx="137" ry="13" fill="#4e554b" opacity=".10" />
    <ellipse cx="160" cy="187" rx="102" ry="7" fill="#4e554b" opacity=".07" />
    {children}
  </svg>;
}

function Desk({ x = 43, y = 129, width = 236 }) {
  return <g>
    <path d={`M${x + 20} ${y + 8}l-5 46h10l8-46M${x + width - 30} ${y + 8}l7 46h10l-4-46`} fill={DARK_WOOD} />
    <path d={`M${x + 3} ${y - 10}h${width - 6}l9 18H${x - 6}z`} fill="#d5b58e" stroke={DARK_WOOD} strokeWidth="2.5" />
    <rect x={x - 6} y={y + 3} width={width + 12} height="10" rx="4" fill={WOOD} />
    <path d={`M${x + 8} ${y + 6}h${width - 16}`} stroke="#e3cba8" strokeWidth="2" />
  </g>;
}

function Notebook({ x, y, width = 61, height = 72, color, angle = 0, open = false }) {
  return <g transform={`translate(${x} ${y}) rotate(${angle})`}>
    {open ? <>
      <path d={`M0 7q${width / 4}-${height / 5} ${width / 2} 0q${width / 4}-${height / 5} ${width / 2} 0v${height}q-${width / 4}-${height / 5}-${width / 2} 0q-${width / 4}-${height / 5}-${width / 2} 0z`} fill={color} stroke={INK} strokeWidth="2.3" />
      <path d={`M4 4q${width / 4 - 4}-${height / 6} ${width / 2 - 4} 2q${width / 4}-${height / 6} ${width / 2 - 4}-2v${height - 8}q-${width / 4}-${height / 6}-${width / 2 - 4} 2q-${width / 4}-${height / 6}-${width / 2 - 4}-2z`} fill={PAPER} />
      <path d={`M${width / 2} 9v${height - 9}M12 18l${width / 2 - 21} 2M12 28l${width / 2 - 21} 2M${width / 2 + 9} 20l${width / 2 - 21}-2M${width / 2 + 9} 30l${width / 2 - 21}-2`} stroke="#c5b99f" strokeWidth="1.8" />
    </> : <>
      <rect x="2" y="3" width={width} height={height} rx="5" fill="#6c604c" opacity=".1" />
      <rect width={width} height={height} rx="5" fill={color} stroke={INK} strokeWidth="2.4" />
      <path d={`M8 0v${height}M15 ${height - 7}h${width - 19}`} stroke={PAPER} strokeWidth="2" />
      {height < 40 ? <path d={`M16 8h${width - 23}M16 12h${width - 23}`} stroke={PAPER} strokeWidth="2" /> : <>
        <rect x="18" y="13" width={width - 27} height="18" rx="2" fill={PAPER} opacity=".9" />
        <path d={`M23 20h${width - 37}M23 25h${width - 41}`} stroke="#b5a991" strokeWidth="1.6" />
      </>}
    </>}
  </g>;
}

function Sheet({ x, y, width = 58, height = 73, angle = 0, color = PAPER }) {
  return <g transform={`translate(${x} ${y}) rotate(${angle})`}>
    <rect x="3" y="3" width={width} height={height} rx="3" fill="#7c715c" opacity=".1" />
    <path d={`M0 0h${width - 12}l12 12v${height - 12}H0z`} fill={color} stroke="#b8aa92" strokeWidth="1.8" />
    <path d={`M${width - 12} 1v11h11`} stroke="#c4b79e" strokeWidth="1.7" />
    {[22, 32, 42, 52].filter((lineY) => lineY < height - 6).map((lineY, i) => <path key={lineY} d={`M10 ${lineY}h${width - 21 - (i > 1 ? 6 : 0)}`} stroke="#c4b79e" strokeWidth="1.7" />)}
  </g>;
}

function Pencil({ x, y, angle = 0, length = 69, color = '#d8ba78' }) {
  return <g transform={`translate(${x} ${y}) rotate(${angle})`}>
    <path d={`M0 0h8v${length - 12}l-4 12-4-12z`} fill={color} stroke={INK} strokeWidth="1.7" />
    <path d={`M0 ${length - 12}h8l-4 12z`} fill={PAPER} /><path d={`m2.5 ${length - 4} 1.5 4 1.5-4`} fill={INK} />
    <path d="M0 8h8" stroke={PAPER} strokeWidth="3" />
  </g>;
}

function Plant({ x, y, p, scale = 1 }) {
  return <g transform={`translate(${x} ${y}) scale(${scale})`}>
    <path d="M0 6v-41M0-11c-19 0-26-15-20-22C-6-33 2-25 0-11M0-23c18-2 25-16 19-24C5-45-3-36 0-23" fill={p.main} stroke="#798e72" strokeWidth="2" />
    <path d="M-18 2h36l-5 29h-26z" fill={p.second} stroke={INK} strokeWidth="2.2" />
    <rect x="-21" y="0" width="42" height="8" rx="3" fill="#d9bda2" stroke={INK} strokeWidth="2" />
  </g>;
}

function Lamp({ x, y, p, scale = 1 }) {
  return <g transform={`translate(${x} ${y}) scale(${scale})`}>
    <path d="M0 0v-57l19-26" stroke={INK} strokeWidth="5" />
    <path d="m8-83 25-13 19 28-38 12z" fill={p.main} stroke={INK} strokeWidth="2.5" />
    <path d="m16-59 33-12" stroke={PAPER} strokeWidth="4" />
    <path d="M-21 0h42l5 7h-52z" fill={p.light} stroke={INK} strokeWidth="2.3" />
  </g>;
}

function Cup({ x, y, p, scale = 1 }) {
  return <g transform={`translate(${x} ${y}) scale(${scale})`}>
    <ellipse cy="15" rx="23" ry="5" fill={p.cream} stroke="#b8a485" strokeWidth="1.6" />
    <path d="M15-10c23-4 22 21-1 18" stroke={INK} strokeWidth="3" />
    <path d="M-17-12h34l-3 23c-7 7-21 7-28 0z" fill={p.light} stroke={INK} strokeWidth="2.5" />
    <ellipse cy="-12" rx="17" ry="5" fill={PAPER} stroke={INK} strokeWidth="2" />
    <ellipse cy="-12" rx="12" ry="2.7" fill="#b29976" />
  </g>;
}

function Calendar({ x, y, p, width = 87, height = 91, marked = false }) {
  return <g transform={`translate(${x} ${y})`}>
    <path d={`M7 ${height - 2}l-8 17h${width + 7}l-8-17`} fill={p.light} stroke={INK} strokeWidth="2.1" />
    <rect width={width} height={height} rx="5" fill={PAPER} stroke={INK} strokeWidth="2.5" />
    <path d={`M0 6q0-6 6-6h${width - 12}q6 0 6 6v17H0z`} fill={p.main} />
    {[18, width - 18].map((v) => <path key={v} d={`M${v}-6v17`} stroke={INK} strokeWidth="4" />)}
    <path d={`M10 42h${width - 20}M10 58h${width - 20}M10 74h${width - 20}M${width / 3} 31v48M${2 * width / 3} 31v48`} stroke="#d2c8b2" strokeWidth="1.8" />
    {marked && <circle cx={width / 2} cy="50" r="11" fill={p.second} opacity=".8" />}
  </g>;
}

function Bag({ x, y, p, scale = 1 }) {
  return <g transform={`translate(${x} ${y}) scale(${scale})`}>
    <path d="M-14-38v-11c0-19 31-19 31 0v11" stroke={INK} strokeWidth="5" />
    <rect x="-35" y="-42" width="72" height="91" rx="17" fill={p.main} stroke={INK} strokeWidth="2.8" />
    <path d="M-35-10h72M-26-28c12-9 43-9 54 0" stroke={p.light} strokeWidth="3" />
    <rect x="-26" y="2" width="54" height="34" rx="7" fill={p.light} stroke={INK} strokeWidth="2.2" />
    <path d="M-17 12h35m-15 0v6" stroke={INK} strokeWidth="2.3" />
    <path d="M-40-24v55m82-55v55" stroke={DARK_WOOD} strokeWidth="5" />
  </g>;
}

function Envelope({ x, y, p, width = 74, angle = 0 }) {
  const h = width * .64;
  return <g transform={`translate(${x} ${y}) rotate(${angle})`}>
    <rect width={width} height={h} rx="4" fill={p.light} stroke={INK} strokeWidth="2.2" />
    <path d={`m2 3 ${width / 2 - 2} ${h / 2}L${width - 2} 3M2 ${h - 3}l${width / 3}-${h / 3}m${width * .31} 0 ${width / 3} ${h / 3}`} stroke={INK} strokeWidth="1.7" />
    <rect x={width - 18} y="7" width="11" height="12" rx="2" fill={p.second} />
  </g>;
}

function Clock({ x, y, p, scale = 1, timer = false }) {
  return <g transform={`translate(${x} ${y}) scale(${scale})`}>
    <path d="m-21 29-8 9m51-9 8 9" stroke={DARK_WOOD} strokeWidth="5" />
    {timer && <><path d="M-7-40H7v7H-7z" fill={p.second} stroke={INK} strokeWidth="2" /><path d="M0-34v8" stroke={INK} strokeWidth="4" /></>}
    <circle r="34" fill={p.main} stroke={INK} strokeWidth="2.7" />
    <circle r="27" fill={PAPER} stroke={INK} strokeWidth="1.5" />
    <path d="M0-19v5m0 33v-5m-19-14h5m33 0h-5M0-15V0l13 8" stroke={INK} strokeWidth="2.5" />
    <circle r="3.3" fill={INK} />
  </g>;
}

function Tools({ x, y, p, scale = 1 }) {
  return <g transform={`translate(${x} ${y}) scale(${scale})`}>
    <path d="M-39 12h81l-4 31h-73z" fill={p.main} stroke={INK} strokeWidth="2.6" />
    <path d="M-44 7h90v10h-90z" fill={p.light} stroke={INK} strokeWidth="2.3" />
    <path d="M-20 5v-33m40 33v-33M-20-29h40v9" stroke={DARK_WOOD} strokeWidth="5" />
    <path d="M-23 5-35-31l9-3 12 39" fill={p.second} stroke={INK} strokeWidth="2.1" />
    <path d="m8 6 9-44 9 2-9 44" fill="#c7ba96" stroke={INK} strokeWidth="2.1" />
    <path d="m17-36-1-15 5-1 2 15" fill={INK} />
    <path d="M-8 25H9" stroke={INK} strokeWidth="3" />
  </g>;
}

function MapSheet({ x, y, p, scale = 1 }) {
  return <g transform={`translate(${x} ${y}) scale(${scale})`}>
    <path d="M0 8 36 0l37 9 35-8v85l-35 8-37-9-36 9z" fill={PAPER} stroke={INK} strokeWidth="2.4" />
    <path d="M36 1v83M73 10v83" stroke="#cbbda3" strokeWidth="2" />
    <path d="M4 59c17-24 34-8 47-25S81 18 105 36" stroke={p.main} strokeWidth="12" opacity=".55" />
    <path d="M14 30c31 14 40 58 73 38" stroke={p.second} strokeWidth="3" strokeDasharray="4 6" />
    <path d="M83 48c-12 0-17 13-1 27 16-14 14-27 1-27z" fill={p.main} stroke={INK} strokeWidth="2" /><circle cx="83" cy="59" r="4" fill={PAPER} />
  </g>;
}

function Infant({ p }) {
  return <g>
    <path d="M60 179h204l-14 12H70z" fill={p.light} />
    <path d="M83 178V43c0-32 103-34 103 0" stroke={DARK_WOOD} strokeWidth="6" />
    <path d="M169 44h35m-18-10v10M139 57h93m-46-13v13" stroke={WOOD} strokeWidth="4" />
    <path d="M141 57v32m44-32v52m45-52v29" stroke={INK} strokeWidth="1.8" />
    <path d="m141 86 6 11 12 2-9 9 2 13-11-6-11 6 2-13-10-9 13-2z" fill={p.cream} stroke={INK} strokeWidth="2" />
    <path d="M185 109c-23 0-25 28 0 30 25-2 23-30 0-30" fill={p.main} stroke={INK} strokeWidth="2" />
    <path d="M231 85c-18-13-30 17-12 28h23c16-12 2-40-11-28" fill={p.second} stroke={INK} strokeWidth="2" />
    <path d="M72 178h25l9 8H62z" fill={WOOD} stroke={DARK_WOOD} strokeWidth="2.5" />
    <g transform="translate(219 163) rotate(20)">
      <path d="M-6-1v28c0 12 12 12 12 0V-1" fill={p.main} stroke={INK} strokeWidth="2.7" />
      <circle cy="-15" r="22" fill={p.cream} stroke={INK} strokeWidth="2.6" />
      <path d="M-12-28c-7 11-5 22 3 31M2-36c10 15 10 29 3 42" stroke={p.second} strokeWidth="5" />
    </g>
  </g>;
}

function Child({ p }) {
  return <g>
    <path d="m45 173 171-14 62 25-187 14z" fill={p.light} />
    <path d="m55 132 43-7 10 48-43 7z" fill={p.main} stroke={INK} strokeWidth="2.5" />
    <circle cx="81" cy="153" r="10" fill={PAPER} />
    <path d="M109 144h43v37h-43zM109 144l10-9h43l-10 9M152 144l10-9v36l-10 10" fill={p.second} stroke={INK} strokeWidth="2.4" />
    <path d="m119 168 12-17 13 17z" fill={p.cream} />
    <path d="m92 109 22-33 23 33z" fill={p.cream} stroke={INK} strokeWidth="2.3" />
    <path d="M221 130c2-26-44-27-39-59 2-14 17-11 18-24" stroke={INK} strokeWidth="1.8" />
    <path d="m211 20 32 33-32 47-28-47z" fill={p.light} stroke={INK} strokeWidth="2.5" />
    <path d="m211 21 1 77M185 53h55" stroke={INK} strokeWidth="1.7" />
    <path d="m211 21 29 32h-28z" fill={p.main} /><path d="m185 54 26 44 1-44z" fill={p.second} />
    <path d="m217 113-10-2 5-7m9 25 10-4-8-5" fill={p.second} stroke={INK} strokeWidth="1.5" />
    <circle cx="241" cy="163" r="26" fill={p.cream} stroke={INK} strokeWidth="2.5" />
    <path d="M223 145c-1 20 8 32 33 36M240 138c-8 14-11 31-8 49" stroke={p.main} strokeWidth="5" />
  </g>;
}

function School({ p }) {
  return <g>
    <Desk x={40} y={123} width={173} />
    <Bag x={260} y={134} p={p} scale={.85} />
    <Notebook x={68} y={84} width={77} height={28} color={p.main} angle={-3} />
    <rect x="127" y="101" width="67" height="17" rx="8" fill={p.second} stroke={INK} strokeWidth="2.3" />
    <path d="M138 109h45m-4 0v4" stroke={PAPER} strokeWidth="2" />
    <Pencil x={159} y={68} length={43} angle={25} />
    <path d="M58 128v24h134v-24" stroke={DARK_WOOD} strokeWidth="3" />
    <path d="M115 141h20" stroke={INK} strokeWidth="3" />
  </g>;
}

function Youth({ p }) {
  return <g>
    <Bag x={83} y={128} p={p} scale={1.1} />
    <MapSheet x={153} y={78} p={p} />
    <Notebook x={184} y={131} width={68} height={48} color={p.second} angle={9} />
    <Pencil x={270} y={126} length={52} angle={19} />
    <path d="M142 178h34" stroke={DARK_WOOD} strokeWidth="4" />
  </g>;
}

function Entry({ p }) {
  return <g>
    <Desk />
    <Calendar x={53} y={26} p={p} width={91} height={89} marked />
    <path d="M174 86h86v36h-86z" fill={p.main} stroke={INK} strokeWidth="2.5" />
    <path d="M174 92h86m-86 12h86" stroke={p.light} strokeWidth="2" />
    <path d="M185 86V56h23l8 7h34v23" fill={p.light} stroke={INK} strokeWidth="2" />
    <Sheet x={198} y={46} width={42} height={38} angle={3} />
    <path d="M208 111h20" stroke={PAPER} strokeWidth="3" />
    <Pencil x={157} y={75} length={47} angle={9} />
  </g>;
}

function Middle({ p }) {
  return <g>
    <path d="M38 45h123v140H38z" fill={WOOD} stroke={DARK_WOOD} strokeWidth="3" />
    <path d="M47 54h105v55H47zm0 65h105v55H47z" fill="#d6c2a4" stroke={DARK_WOOD} strokeWidth="2" />
    {[0, 1, 2, 3].map((n) => <g key={n}>
      <rect x={54 + n * 22} y={66 + n % 2 * 6} width="17" height={41 - n % 2 * 6} rx="2" fill={n % 2 ? p.second : p.main} stroke={INK} strokeWidth="1.6" />
      <path d={`M${58 + n * 22} 91h9`} stroke={PAPER} strokeWidth="2" />
    </g>)}
    <path d="M58 139h75v28H58z" fill={p.light} stroke={INK} strokeWidth="2" /><path d="M85 151h19" stroke={INK} strokeWidth="3" />
    <Clock x={239} y={105} p={p} scale={.95} />
    <Tools x={222} y={153} p={p} scale={.85} />
  </g>;
}

function Transition({ p }) {
  return <g>
    <Desk />
    <Lamp x={81} y={113} p={p} scale={.9} />
    <Notebook x={121} y={82} width={93} height={37} color={p.main} open />
    <Plant x={252} y={92} p={p} scale={.85} />
    <Pencil x={208} y={96} length={41} angle={-46} />
    <Notebook x={56} y={151} width={76} height={24} color={p.second} angle={-2} />
  </g>;
}

function Later({ p }) {
  return <g>
    <path d="M50 121 40 65c-4-25 76-30 86-9l15 67" fill={p.main} stroke={INK} strokeWidth="3" />
    <path d="M43 127v55m96-55 7 55" stroke={DARK_WOOD} strokeWidth="8" />
    <path d="M45 118c19-8 74-9 93-1l5 24H39z" fill={p.light} stroke={INK} strokeWidth="2.7" />
    <path d="M37 102v29m110-29v28" stroke={DARK_WOOD} strokeWidth="6" />
    <path d="M29 100h28m78 0h24" stroke={WOOD} strokeWidth="8" />
    <Notebook x={59} y={90} width={66} height={24} color={p.second} open />
    <Desk x={188} y={137} width={94} />
    <Cup x={225} y={115} p={p} scale={.9} />
    <Notebook x={194} y={163} width={80} height={24} color={p.second} angle={-4} />
    <rect x="220" y="167" width="24" height="13" rx="2" fill={PAPER} transform="rotate(-4 220 167)" />
  </g>;
}

const AGE_ART = { infant: Infant, child: Child, school: School, youth: Youth, entry: Entry, middle: Middle, transition: Transition, later: Later };

/** 나이 구간에 맞는 생활 도구만 표현하며 가족·주거·성과를 가정하지 않는다. */
export function AgeObjects({ stage, variant = 0 }) {
  const { p } = palette(variant);
  const Art = Object.prototype.hasOwnProperty.call(AGE_ART, stage) ? AGE_ART[stage] : null;
  return <ArtFrame>{Art && <Art p={p} />}</ArtFrame>;
}

function Mentor({ p }) {
  return <g>
    <Desk />
    <Notebook x={57} y={72} width={116} height={43} color={p.main} open />
    <Lamp x={232} y={114} p={p} />
    <path d="m177 107 30-27m-30 27 15-2m-15 2 2-14" stroke={p.second} strokeWidth="4" />
    <Notebook x={72} y={151} width={95} height={28} color={p.second} angle={-3} />
  </g>;
}

function Friend({ p }) {
  return <g>
    <path d="m47 123 176-22 64 78-199 17z" fill={p.light} stroke={INK} strokeWidth="2" />
    <path d="m63 140 180-22m-162 43 180-22m-160 43 178-22M90 120l52 71m-12-78 55 73m-16-78 56 73m-17-78 55 73" stroke={p.main} strokeWidth="5" opacity=".42" />
    <path d="m91 97 108-7 38 59-121 12z" fill={WOOD} stroke={INK} strokeWidth="2.5" />
    <path d="m101 101 94-6 31 49-106 11z" fill={PAPER} stroke={INK} strokeWidth="1.5" />
    <path d="m116 113 68-5 18 27-70 7z" fill={p.light} stroke={p.main} strokeWidth="10" strokeDasharray="9 8" />
    <path d="m139 112-5 17h13zM176 125l-5 18h14z" fill={p.second} stroke={INK} strokeWidth="1.6" />
    <rect x="60" y="146" width="21" height="21" rx="4" fill={PAPER} stroke={INK} strokeWidth="2" /><circle cx="66" cy="152" r="2" fill={INK} /><circle cx="75" cy="161" r="2" fill={INK} />
    <Cup x={254} y={115} p={p} scale={.7} />
  </g>;
}

function Rival({ p }) {
  return <g>
    <Desk x={34} y={136} width={252} />
    <path d="m48 99 136-7 35 37-156 6z" fill={WOOD} stroke={INK} strokeWidth="2.4" />
    {[0, 1, 2, 3].map((row) => [0, 1, 2, 3, 4, 5].map((col) => <path key={`${row}-${col}`} d={`m${52 + col * 22 + row * 4} ${101 + row * 7} 21-1.1 4 6.5-22 1.1z`} fill={(row + col) % 2 ? p.main : PAPER} />))}
    <path d="M94 87h14l-2 17H96zM91 103h20v7H91zM97 86v-9h8v9" fill={p.second} stroke={INK} strokeWidth="2" />
    <path d="m137 88 6-21 11 5 6 17-3 11h-22zM132 99h30v8h-30z" fill={p.light} stroke={INK} strokeWidth="2" />
    <Clock x={256} y={91} p={p} scale={.8} timer />
  </g>;
}

function Colleague({ p }) {
  return <g>
    <Desk />
    <Notebook x={53} y={80} width={91} height={34} color={p.main} open />
    <Tools x={231} y={81} p={p} scale={.85} />
    <path d="m155 105 17-23 16 23zM149 105h44v14h-44z" fill={p.cream} stroke={INK} strokeWidth="2.1" />
    <Pencil x={80} y={61} length={44} angle={-28} />
    <Pencil x={188} y={98} length={40} angle={-59} color={p.second} />
  </g>;
}

function Partner({ p }) {
  return <g>
    <Desk x={49} y={136} width={222} />
    <Envelope x={122} y={98} p={p} width={83} angle={-6} />
    <Sheet x={151} y={58} width={53} height={48} angle={6} />
    <Cup x={79} y={113} p={p} />
    <g transform="translate(254 111) scale(-1 1)"><Cup x={0} y={0} p={{ ...p, light: p.second }} /></g>
    <path d="M112 115h11m-6-6v12" stroke={p.main} strokeWidth="2.5" />
  </g>;
}

function Junior({ p }) {
  return <g>
    <Desk />
    <Notebook x={66} y={62} width={124} height={52} color={p.main} open />
    <path d="m85 86 11-9 12 9-12 10zM142 82h20v18h-20z" fill={p.light} stroke={p.main} strokeWidth="2" />
    <Pencil x={232} y={46} length={77} angle={15} />
    <Notebook x={202} y={97} width={47} height={24} color={p.second} angle={4} />
    <path d="m108 162 37-13 31 13-31 14z" fill={p.light} stroke={INK} strokeWidth="2" />
    <path d="m145 150 0 25" stroke={INK} strokeWidth="1.6" />
  </g>;
}

function RelationshipMark({ state, p }) {
  if (state === 'promised') return <g transform="translate(267 159)">
    <rect x="-23" y="-27" width="46" height="46" rx="4" fill={PAPER} stroke={INK} strokeWidth="2" />
    <path d="M-23-14h46m-33-19v12m20-12v12" stroke={p.main} strokeWidth="4" />
    <path d="M-12-5h8m9 0h8M-12 5h8m9 0h8" stroke="#b9ab91" strokeWidth="2" />
    <circle cx="9" cy="5" r="9" stroke={p.second} strokeWidth="3" />
  </g>;
  if (state === 'kept') return <g transform="translate(266 162)">
    <path d="M-24-24h40l7 8v34h-47z" fill={PAPER} stroke={INK} strokeWidth="2" />
    <path d="M-15-13H9m-24 8H1" stroke="#b6aa94" strokeWidth="2" />
    <circle cx="11" cy="11" r="16" fill={p.light} stroke={p.main} strokeWidth="2.2" />
    <path d="m3 11 6 6 11-13" stroke={p.main} strokeWidth="3.5" />
  </g>;
  if (state === 'broken') return <g transform="translate(268 162)">
    <path d="M-25-25h47v44h-47z" fill={p.cream} stroke={INK} strokeWidth="2" />
    <path d="M-16-14H9m-25 9H2m-18 9h13" stroke="#a89678" strokeWidth="2" />
    <Pencil x={16} y={-12} length={36} angle={28} color={p.second} />
    <path d="m-2 11 12-8m-12 8 8 1m-8-1 3-8" stroke={p.main} strokeWidth="2.3" />
  </g>;
  return null;
}

const RELATION_ART = { '멘토': Mentor, '친구': Friend, '라이벌': Rival, '동료': Colleague, '연인': Partner, '후배': Junior };

/** 역할과 실제 약속 상태를 받아 그린다. 관계 수나 신뢰 판정은 여기서 만들지 않는다. */
export function RelationshipObjects({ role, state = 'none', variant = 0 }) {
  const { p } = palette(variant);
  const Art = Object.prototype.hasOwnProperty.call(RELATION_ART, role) ? RELATION_ART[role] : null;
  return <ArtFrame>{Art && <Art p={p} />}<RelationshipMark state={state} p={p} /></ArtFrame>;
}

function Presentation({ p }) {
  return <g>
    <path d="m67 180 20-97m91 97-19-97m-42 12v87" stroke={DARK_WOOD} strokeWidth="6" />
    <rect x="41" y="28" width="157" height="103" rx="5" fill={PAPER} stroke={WOOD} strokeWidth="5" />
    <path d="M58 111h120M60 45h72m-72 9h45" stroke="#beb49c" strokeWidth="2.7" />
    <path d="M66 104V84h20v20m14 0V71h20v33m14 0V61h20v43" fill={p.light} stroke={p.main} strokeWidth="2" />
    <path d="m66 75 39-18 33 3 33-21m-13 0h13v13" stroke={p.second} strokeWidth="3.5" />
    <path d="M251 173V94m-25 89h50m-25-10-20 10m20-10 20 10" stroke={INK} strokeWidth="4" />
    <g transform="translate(247 69) rotate(20)"><rect x="-13" y="-22" width="26" height="47" rx="13" fill={p.main} stroke={INK} strokeWidth="2.7" /><path d="M-9-10H9m-18 8H9m-18 8H9" stroke={p.light} strokeWidth="2" /><path d="M-19 8c-3 32 42 32 38 0" stroke={INK} strokeWidth="3" /></g>
  </g>;
}

function Schedule({ p }) {
  return <g>
    <Calendar x={48} y={35} p={p} width={136} height={135} marked />
    <Notebook x={203} y={97} width={66} height={81} color={p.second} angle={7} />
    <Pencil x={284} y={92} length={82} angle={14} />
    <path d="M65 129h27m14 0h27m14 0h17m-99 17h27m14 0h27" stroke={p.main} strokeWidth="3" />
    <path d="m145 80 5 5 10-11" stroke={p.second} strokeWidth="3" />
  </g>;
}

function Budget({ p }) {
  return <g>
    <Desk />
    <Envelope x={47} y={66} p={p} width={86} angle={-6} />
    <Envelope x={81} y={92} p={{ ...p, light: p.cream }} width={86} angle={3} />
    <rect x="186" y="42" width="78" height="83" rx="8" fill={p.main} stroke={INK} strokeWidth="2.5" />
    <rect x="197" y="52" width="56" height="19" rx="3" fill={PAPER} stroke={INK} strokeWidth="1.8" />
    {[0, 1, 2].map((row) => [0, 1, 2, 3].map((col) => <rect key={`${row}-${col}`} x={197 + col * 15} y={80 + row * 12} width="10" height="8" rx="2" fill={col === 3 ? p.second : p.light} />))}
    <Sheet x={65} y={146} width={98} height={36} angle={-3} />
    <path d="M86 157h54m-54 9h43m-43 9h48" stroke={p.main} strokeWidth="2" />
  </g>;
}

function Meeting({ p }) {
  return <g>
    <path d="M59 124v-27c0-25 43-25 43 0v23m116 4V97c0-25 43-25 43 0v23" fill={p.light} stroke={INK} strokeWidth="2.7" />
    <path d="M71 158v24m177-24v24m-88-20v22" stroke={DARK_WOOD} strokeWidth="7" />
    <ellipse cx="160" cy="124" rx="119" ry="32" fill="#d6b78e" stroke={DARK_WOOD} strokeWidth="2.7" />
    <path d="M41 123v10c8 42 229 42 238 0v-10" fill={WOOD} stroke={DARK_WOOD} strokeWidth="2" />
    <ellipse cx="160" cy="122" rx="119" ry="31" fill="#d8bc96" stroke={DARK_WOOD} strokeWidth="2.3" />
    <Envelope x={120} y={99} p={p} width={71} angle={-4} />
    <Cup x={79} y={116} p={p} scale={.6} /><Cup x={237} y={113} p={p} scale={.6} />
    <Sheet x={130} y={40} width={60} height={51} angle={-3} />
    <path d="M142 52h30m-30 8h20" stroke={p.main} strokeWidth="2" />
  </g>;
}

function Collaboration({ p }) {
  return <g>
    <Desk />
    <path d="M54 65h36c-12 20 24 20 12 0h35v31c-19-10-19 23 0 14v12H54z" fill={p.main} stroke={INK} strokeWidth="2.4" />
    <path d="M137 65h36v14c19-9 19 22 0 13v30h-36v-12c-19 9-19-24 0-14z" fill={p.cream} stroke={INK} strokeWidth="2.4" />
    <path d="M93 28h33v16c21-11 21 21 0 12v24h-12c8-19-23-19-15 0H80V56h13z" fill={p.second} stroke={INK} strokeWidth="2.4" transform="rotate(-12 110 54)" />
    <Tools x={233} y={82} p={p} scale={.85} />
    <Pencil x={184} y={104} length={34} angle={-55} />
    <path d="M84 158h52m10 0h56m-87 11h60" stroke={p.main} strokeWidth="4" />
  </g>;
}

function Practice({ p }) {
  return <g>
    <Desk />
    <Tools x={80} y={78} p={p} />
    <path d="m158 104 47-24 54 25-49 24z" fill={p.light} stroke={INK} strokeWidth="2.3" />
    <path d="M178 101V76l28-14 30 15v27m-58-28 28 15 30-14m-30 14v25" fill={p.cream} stroke={INK} strokeWidth="2.5" />
    <path d="m177 75 29-32 31 34" stroke={WOOD} strokeWidth="6" />
    <path d="m177 75 29-32 31 34m-31-34v19" stroke={INK} strokeWidth="1.5" />
    <path d="M232 104v-9m-7-6 14 7" stroke={p.second} strokeWidth="4" />
    <path d="m149 159 64-5 2 13-64 5z" fill={p.second} stroke={INK} strokeWidth="2" />
    <path d="m159 159 1 8m9-9 1 8m9-9 1 8m9-9 1 8m9-9 1 8" stroke={INK} strokeWidth="1.4" />
  </g>;
}

function Travel({ p }) {
  return <g>
    <Bag x={84} y={129} p={p} scale={1.06} />
    <MapSheet x={152} y={72} p={p} />
    <Notebook x={180} y={153} width={58} height={31} color={p.second} angle={-5} />
    <circle cx="264" cy="164" r="22" fill={p.cream} stroke={INK} strokeWidth="2.4" />
    <circle cx="264" cy="164" r="16" fill={PAPER} stroke="#b9aa8e" strokeWidth="1.4" />
    <path d="m265 147 6 23-7-4-8 5z" fill={p.main} stroke={INK} strokeWidth="1.5" />
    <path d="M264 143v5m0 32v5m-22-21h5m34 0h5" stroke={INK} strokeWidth="1.6" />
  </g>;
}

function Review({ p }) {
  return <g>
    <Desk />
    <Sheet x={60} y={47} width={71} height={76} angle={-8} />
    <Sheet x={145} y={43} width={71} height={76} angle={5} />
    <path d="m78 94 5 5 10-12m70 9 5 5 10-12" stroke={p.main} strokeWidth="3" />
    <path d="M84 112h32m47-1h29" stroke={p.second} strokeWidth="3" />
    <path d="m235 103 25 35" stroke={DARK_WOOD} strokeWidth="12" />
    <path d="m235 103 25 35" stroke={WOOD} strokeWidth="7" />
    <circle cx="220" cy="82" r="31" fill={p.light} fillOpacity=".55" stroke={INK} strokeWidth="5" />
    <circle cx="220" cy="82" r="24" stroke={PAPER} strokeWidth="2.5" />
    <path d="M205 74h26m-26 9h21m-21 9h29" stroke={p.main} strokeWidth="2.2" />
    <Notebook x={79} y={151} width={101} height={29} color={p.second} angle={-2} />
  </g>;
}

const ACTION_ART = { presentation: Presentation, schedule: Schedule, budget: Budget, meeting: Meeting, collaboration: Collaboration, practice: Practice, travel: Travel, review: Review };

/** 선택 기록에서 전달된 활동 종류만 표현한다. 기존 활동 소품도 그대로 지원한다. */
export function ActionObjects({ kind, variant = 0 }) {
  const { p } = palette(variant);
  const Art = Object.prototype.hasOwnProperty.call(ACTION_ART, kind) ? ACTION_ART[kind] : null;
  if (!Art) return <SceneProps kind={kind} variant={variant} />;
  return <ArtFrame><Art p={p} /></ArtFrame>;
}
